import { useOrganisationList } from "@inrange/building-manager-api-client";
import {
  getPpas,
  ppaErrorString,
  ppaStartingTariff,
  ppaUpdate,
  RecursivePartialSiteAllowStringValues,
  SiteAllowStringValues,
} from "@inrange/building-manager-api-client/models-site";
import {
  SiteCalculationsContext,
  useContextTS,
} from "@inrange/shared-components";
import { BorderedCheckbox } from "@inrange/theme-components";
import { Icons } from "@inrange/theme-components/icons";
import { useEffect } from "react";
import { Button } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import FormInputPercentage from "../../FormInputPercentage";
import CollapsibleCard from "../CollapsibleCard";
import { PPA_TYPE_LABELS } from "./enums";
import { parseAsNumber } from "./utils";

const ExportNetworkTariff = ({
  site,
  setSite,
  isBulkEdit,
}: {
  site: SiteAllowStringValues;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  isBulkEdit?: boolean;
}) => {
  const { errors, setClientsideValidations, siteCalculations } = useContextTS(
    SiteCalculationsContext
  );
  const { fetchOrganisations } = useOrganisationList();
  const orgsById = (fetchOrganisations.data?.organisations || []).reduce(
    (acc, org) => {
      acc[org.id] = org;
      return acc;
    },
    {}
  );
  const maxNetworkImportTariff = (site.siteOwnerships || [])
    .filter(
      (so) =>
        (so.ownership === "tenant" || so.ownership === "ownerOccupier") &&
        orgsById[so.orgID] !== undefined &&
        orgsById[so.orgID].maxNetworkImportTariff !== undefined
    )
    .map((so) => orgsById[so.orgID].maxNetworkImportTariff)
    .reduce((acc, maxNetworkImportTariff) => {
      if (acc === undefined) {
        return maxNetworkImportTariff;
      }
      // If there are multiple maxNetworkImportTariff values, return the lowest one
      return maxNetworkImportTariff < acc ? maxNetworkImportTariff : acc;
    }, undefined);
  const isNetworkImportTariffOutOfBounds =
    maxNetworkImportTariff &&
    parseAsNumber(site.tariffConfig.networkImportTariff) >
      maxNetworkImportTariff;
  useEffect(() => {
    setClientsideValidations((prev) => ({
      ...prev,
      isNetworkImportTariffOutOfBounds: isNetworkImportTariffOutOfBounds,
    }));
  }, [isNetworkImportTariffOutOfBounds, setClientsideValidations]);

  // "Zero export enforced?" distinguishes a site that physically exports nothing from one
  // modelled with a non-zero export limit / that merely earns nothing for spill. Only
  // relevant where there's a modelling-vs-reality mismatch: an installation-in-progress /
  // operational site with a zero actual spill tariff but a non-zero modelled export limit.
  const actualSpillTariffRaw = site.tariffConfig.actualSpillTariff;
  const actualSpillTariffIsZero =
    actualSpillTariffRaw !== undefined &&
    actualSpillTariffRaw !== null &&
    `${actualSpillTariffRaw}` !== "" &&
    Number(actualSpillTariffRaw) === 0;
  // We need at least one esys with a *defined* export limit > 0. The hasAll/hasAnyZero
  // helpers don't express this: !hasAllZeroExportLimit treats an undefined limit as
  // "non-zero", and hasAnyZeroExportLimit is true when an esys IS zero - neither matches.
  const someEsysHasExportLimit = Object.values(site.esys || {})
    .filter((esys) => !!esys)
    .some((esys) => parseAsNumber(esys.exportLimit) > 0);
  const showZeroExportEnforced =
    ["installationInProgress", "operational"].includes(
      site.operationalStatus as string
    ) &&
    actualSpillTariffIsZero &&
    someEsysHasExportLimit;

  return (
    <CollapsibleCard
      title={"Export & network tariffs"}
      className="mt-2"
      isBulkEdit={isBulkEdit}
    >
      {getPpas(site.tariffConfig.spillExportPPA, "other").map((ppa, index) => (
        <Row
          className="d-flex flex-wrap align-items-end"
          key={`spillExport-ppa-${index}`}
        >
          <Col sm={4}>
            <Form.Group
              className="mb-3"
              controlId={`spillExportPPATariff-${index}`}
              style={{
                display: "grid",
                gridTemplateColumns:
                  !ppa.nextPpa && index > 0
                    ? "auto 32px 32px"
                    : !ppa.nextPpa && index == 0
                      ? "auto 32px"
                      : "auto",
                gridTemplateRows: "auto auto auto",
                gap: "0 8px",
              }}
            >
              <Form.Label
                style={{
                  gridRow: 1,
                  gridColumn:
                    !ppa.nextPpa && index > 0
                      ? "span 3"
                      : !ppa.nextPpa && index == 0
                        ? "span 2"
                        : 1,
                }}
              >
                Spill export tariff{index > 0 && ` ${index + 1}`}
              </Form.Label>
              <Form.Control
                type="text"
                style={{ gridRow: 2, gridColumn: 1 }}
                value={
                  siteCalculations.hasAllZeroExportLimit ? "0" : ppa.tariff
                }
                onChange={(e) =>
                  setSite({
                    tariffConfig: {
                      spillExportPPA: ppaUpdate(index, {
                        tariff: e.target.value,
                      }),
                    },
                  })
                }
                disabled={siteCalculations.hasAllZeroExportLimit}
                isInvalid={
                  !!errors[
                    `tariffConfig.spillExportPPA${ppaErrorString(index)}.tariff`
                  ]
                }
              />
              <Form.Control.Feedback
                type="invalid"
                style={{ gridRow: 3, gridColumn: 1 }}
              >
                {
                  errors[
                    `tariffConfig.spillExportPPA${ppaErrorString(index)}.tariff`
                  ]
                }
              </Form.Control.Feedback>
              {!ppa.nextPpa && (
                <Button
                  style={{ gridRow: 2, gridColumn: index === 0 ? 2 : 3 }}
                  variant="success"
                  onClick={() =>
                    setSite({
                      tariffConfig: {
                        spillExportPPA: ppaUpdate(index, {
                          nextPpa: {
                            tariff: ppaStartingTariff(
                              parseAsNumber(ppa.tariff),
                              parseAsNumber(ppa.ppaLength),
                              parseAsNumber(site.energyPriceInflationRate),
                              site.tariffConfig.spillExportPPA
                                ?.ppaCrossPpaIndexationType ?? "compound"
                            ),
                            ppaContractType: ppa.ppaContractType || "other",
                            ppaLength: ppa.ppaLength,
                            ppaType: ppa.ppaType || "indexed",
                            ppaIndex: ppa.ppaIndex,
                            nextPpa: undefined,
                          },
                        }),
                      },
                    })
                  }
                >
                  +
                </Button>
              )}
              {index > 0 && !ppa.nextPpa && (
                <div
                  onClick={() => {
                    setSite({
                      tariffConfig: {
                        spillExportPPA: ppaUpdate(index - 1, {
                          nextPpa: null as unknown as undefined,
                        }),
                      },
                    });
                  }}
                  style={{
                    gridRow: 2,
                    gridColumn: 2,
                    cursor: "pointer",
                    alignSelf: "center",
                  }}
                >
                  <img
                    alt="remove"
                    src={Icons.trashcanRed}
                    style={{
                      width: "20px",
                      height: "20px",
                      marginLeft: "6px",
                      cursor: "pointer",
                    }}
                  ></img>
                </div>
              )}
            </Form.Group>
          </Col>
          <Col sm={4}>
            <Form.Group
              className="mb-3"
              controlId={`spillExportPPALength-${index}`}
            >
              <Form.Label>Export PPA length (years)</Form.Label>
              <Form.Control
                type="text"
                value={
                  parseFloat(site.totalInstalledCapacity as string) === 0
                    ? ""
                    : ppa.ppaLength
                }
                onChange={(e) =>
                  setSite({
                    tariffConfig: {
                      spillExportPPA: ppaUpdate(index, {
                        ppaLength: e.target.value,
                      }),
                    },
                  })
                }
                isInvalid={
                  !!errors[
                    `tariffConfig.spillExportPPA${ppaErrorString(index)}.ppaLength`
                  ]
                }
                disabled={
                  parseFloat(site.totalInstalledCapacity as string) === 0
                }
              />
              <Form.Control.Feedback type="invalid">
                {
                  errors[
                    `tariffConfig.spillExportPPA${ppaErrorString(index)}.ppaLength`
                  ]
                }
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={4}>
            <Form.Label>Export PPA type</Form.Label>
            <Form
              style={{
                display: "flex",
                fontSize: 12,
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              {Object.entries(PPA_TYPE_LABELS).map(([key, label]) => (
                <BorderedCheckbox
                  key={`spillExport-${key}-${index}`}
                  dataTestId={`spillExport-${key}-${index}`}
                  formName="ppa_type"
                  value={key}
                  label={label}
                  selectedOption={ppa.ppaType}
                  setSelectedOption={(value) =>
                    setSite({
                      tariffConfig: {
                        spillExportPPA: ppaUpdate(index, {
                          ppaType: value,
                          ppaIndex: value === "fixed" ? 0 : ppa.ppaIndex,
                        }),
                      },
                    })
                  }
                  disabled={
                    parseFloat(site.totalInstalledCapacity as string) === 0
                  }
                />
              ))}
            </Form>
          </Col>
          {ppa.ppaType === "indexed" && (
            <Col sm={4}>
              <Form.Group
                className="mb-3"
                controlId={`spillExportPPAIndex-${index}`}
              >
                <Form.Label>Export PPA index (%)</Form.Label>
                <FormInputPercentage
                  value={
                    parseFloat(site.totalInstalledCapacity as string) === 0
                      ? ""
                      : (ppa.ppaIndex ?? "")
                  }
                  onChange={(e) =>
                    setSite({
                      tariffConfig: {
                        spillExportPPA: ppaUpdate(index, {
                          ppaIndex: e.target.value,
                        }),
                      },
                    })
                  }
                  disabled={
                    parseFloat(site.totalInstalledCapacity as string) === 0
                  }
                  errorKey={`tariffConfig.spillExportPPA${ppaErrorString(index)}.ppaIndex`}
                />
              </Form.Group>
            </Col>
          )}
        </Row>
      ))}
      <Row className="d-flex flex-wrap">
        <Col sm={4}>
          <Form.Group className="mb-3" controlId="networkImportTariff">
            <Form.Label>Network import tariff</Form.Label>
            <Form.Control
              type="text"
              value={site.tariffConfig.networkImportTariff ?? ""}
              onChange={(e) =>
                setSite({
                  tariffConfig: { networkImportTariff: e.target.value },
                })
              }
              isInvalid={
                !!errors["tariffConfig.networkImportTariff"] ||
                isNetworkImportTariffOutOfBounds
              }
            />
            <Form.Control.Feedback type="invalid">
              {errors["tariffConfig.networkImportTariff"] ||
                (isNetworkImportTariffOutOfBounds
                  ? `Max network import tariff is ${maxNetworkImportTariff}`
                  : undefined)}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3" controlId="actualSpillTariff">
            <Form.Label>Actual spill tariff</Form.Label>
            <Form.Control
              type="text"
              value={site.tariffConfig.actualSpillTariff ?? ""}
              onChange={(e) =>
                setSite({
                  tariffConfig: {
                    actualSpillTariff: e.target.value,
                  },
                })
              }
              isInvalid={!!errors["tariffConfig.actualSpillTariff"]}
            />
            <Form.Control.Feedback type="invalid">
              {errors["tariffConfig.actualSpillTariff"]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        {showZeroExportEnforced && (
          <Col sm={4}>
            <Form.Group className="mb-3" controlId="zeroExportEnforced">
              <Form.Label>Zero export enforced?</Form.Label>
              <Form.Check
                type="checkbox"
                id="zeroExportEnforced"
                label="Physically enforces zero grid export"
                checked={!!site.tariffConfig.zeroExportEnforced}
                onChange={(e) =>
                  setSite({
                    tariffConfig: { zeroExportEnforced: e.target.checked },
                  })
                }
              />
            </Form.Group>
          </Col>
        )}
      </Row>
    </CollapsibleCard>
  );
};

export default ExportNetworkTariff;
